import { useState, useEffect, useRef } from "react";
import { APP_CONFIG } from "../../appConfig";
import { sendResendEmail } from "../../utils/email";
import { DEMO_MODE } from "../../utils/firebase";
import { VersionTag } from "../shared/VersionTag";

export function PlannerSettings({ settings, onSettingsChange, users, data, onDataChange }) {
  const children = users.filter(u => u.role === "child");
  const [nameInput, setNameInput] = useState(settings.appName || "SpellWise Kids");
  const [nameSaved, setNameSaved] = useState(false);
  const [apiKey, setApiKey] = useState(settings.geminiKey || "");
  const [apiKeySaved, setApiKeySaved] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [geminiModel, setGeminiModel] = useState(settings.geminiModel || "gemini-1.5-flash");
  const [liveModels, setLiveModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsErr, setModelsErr] = useState("");
  const [testStatus, setTestStatus] = useState(""); // "", "testing", "ok", "fail"
  const [testMsg, setTestMsg] = useState("");

  const FALLBACK_MODELS = [
    { id: "gemini-2.5-flash",      label: "Gemini 2.5 Flash",      sub: "Recommended · fast · great for photos" },
    { id: "gemini-2.5-flash-lite", label: "Gemini 2.5 Flash Lite", sub: "Lightest · lowest cost" },
    { id: "gemini-2.0-flash",      label: "Gemini 2.0 Flash",      sub: "Retiring June 2026" },
  ];

  async function fetchGeminiModels(key) {
    if (!key) return;
    setModelsLoading(true); setModelsErr("");
    try {
      const res = await fetch(`https://generativelanguage.googleapis.com/v1beta/models?key=${key}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const filtered = (json.models || [])
        .filter(m => m.supportedGenerationMethods?.includes("generateContent") && m.name.includes("gemini"))
        .map(m => ({
          id: m.name.replace("models/", ""),
          label: m.displayName || m.name.replace("models/", ""),
          sub: m.description ? m.description.slice(0, 55) + (m.description.length > 55 ? "…" : "") : "",
        }))
        .sort((a, b) => a.id.localeCompare(b.id));
      setLiveModels(filtered.length ? filtered : FALLBACK_MODELS);
    } catch (e) {
      setModelsErr("Couldn't load models — showing defaults."); setLiveModels(FALLBACK_MODELS);
    } finally { setModelsLoading(false); }
  }

  const settingsKeyRef = useRef(settings.geminiKey);
  useEffect(() => { if (settingsKeyRef.current) fetchGeminiModels(settingsKeyRef.current); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function testGeminiConnection() {
    const key = apiKey || settings.geminiKey;
    if (!key) { setTestStatus("fail"); setTestMsg("No API key saved."); setTimeout(() => setTestStatus(""), 4000); return; }
    setTestStatus("testing"); setTestMsg("");
    try {
      const model = geminiModel || "gemini-1.5-flash";
      const res = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${key}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contents: [{ parts: [{ text: "Reply with just the word OK." }] }] }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e?.error?.message || `HTTP ${res.status}`); }
      const json = await res.json();
      const reply = json?.candidates?.[0]?.content?.parts?.[0]?.text?.trim() || "OK";
      setTestStatus("ok"); setTestMsg(`✅ Connected! Model replied: "${reply.slice(0, 40)}"`);
    } catch (e) {
      setTestStatus("fail"); setTestMsg(`❌ ${e.message}`);
    }
    setTimeout(() => { setTestStatus(""); setTestMsg(""); }, 6000);
  }

  const displayModels = liveModels.length ? liveModels : FALLBACK_MODELS;

  // Resend settings
  const rs = settings.resend || {};
  const [resendApiKey,  setResendApiKey]  = useState(rs.apiKey || "");
  const [resendFrom,    setResendFrom]    = useState(rs.fromEmail || "noreply@sbajaj.app");
  const [resendParent1, setResendParent1] = useState(rs.parent1Email || "");
  const [resendParent2, setResendParent2] = useState(rs.parent2Email || "");
  const [rewardWorkerUrl, setRewardWorkerUrl] = useState(settings.rewardWorkerUrl || APP_CONFIG.rewardActionUrl || "");
  const [resendSaved,   setResendSaved]   = useState(false);
  const [resendTestMsg, setResendTestMsg] = useState("");

  // Email template editor
  const DEFAULT_TEMPLATES = {
    spelling: `<!-- MERGE TAGS: {{child_avatar}} {{child_name}} {{quiz_type}} {{score}} {{score_color}} {{time}} {{pts_earned}} {{mastered_html}} {{words_to_review_html}} {{date}} -->
<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;padding:24px;color:#1e293b">
  <div style="font-size:22px;font-weight:800;margin-bottom:6px">{{child_avatar}} {{child_name}} — {{quiz_type}}</div>
  <div style="font-size:15px;color:#64748b;margin-bottom:24px">{{date}}</div>
  <div style="display:flex;gap:16px;flex-wrap:wrap;margin-bottom:8px">
    <div style="background:#f1f5f9;border-radius:10px;padding:14px 20px;text-align:center;min-width:90px">
      <div style="font-size:28px;font-weight:900;color:{{score_color}}">{{score}}%</div>
      <div style="font-size:14px;color:#64748b;font-weight:600">Score</div>
    </div>
    <div style="background:#f1f5f9;border-radius:10px;padding:14px 20px;text-align:center;min-width:90px">
      <div style="font-size:28px;font-weight:900;color:#1e293b">{{time}}</div>
      <div style="font-size:14px;color:#64748b;font-weight:600">Time</div>
    </div>
    <div style="background:#fef3c7;border-radius:10px;padding:14px 20px;text-align:center;min-width:90px">
      <div style="font-size:28px;font-weight:900;color:#d97706">+{{pts_earned}}</div>
      <div style="font-size:14px;color:#92400e;font-weight:600">Points</div>
    </div>
  </div>
  {{mastered_html}}
  {{words_to_review_html}}
  <div style="margin-top:32px;padding-top:16px;border-top:1px solid #e5e7eb;font-size:14px;color:#94a3b8">Sent automatically by SpellWise Kids</div>
</div>`,
    meaning: `<!-- MERGE TAGS: {{child_avatar}} {{child_name}} {{quiz_type}} {{score}} {{score_color}} {{time}} {{pts_earned}} {{words_to_review_html}} {{date}} -->
<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;padding:24px;color:#1e293b">
  <div style="font-size:22px;font-weight:800;margin-bottom:6px">{{child_avatar}} {{child_name}} — {{quiz_type}}</div>
  <div style="font-size:15px;color:#64748b;margin-bottom:24px">{{date}}</div>
  <div style="display:flex;gap:16px;flex-wrap:wrap;margin-bottom:8px">
    <div style="background:#f1f5f9;border-radius:10px;padding:14px 20px;text-align:center;min-width:90px">
      <div style="font-size:28px;font-weight:900;color:{{score_color}}">{{score}}%</div>
      <div style="font-size:14px;color:#64748b;font-weight:600">Score</div>
    </div>
    <div style="background:#f1f5f9;border-radius:10px;padding:14px 20px;text-align:center;min-width:90px">
      <div style="font-size:28px;font-weight:900;color:#1e293b">{{time}}</div>
      <div style="font-size:14px;color:#64748b;font-weight:600">Time</div>
    </div>
    <div style="background:#fef3c7;border-radius:10px;padding:14px 20px;text-align:center;min-width:90px">
      <div style="font-size:28px;font-weight:900;color:#d97706">+{{pts_earned}}</div>
      <div style="font-size:14px;color:#92400e;font-weight:600">Points</div>
    </div>
  </div>
  {{words_to_review_html}}
  <div style="margin-top:32px;padding-top:16px;border-top:1px solid #e5e7eb;font-size:14px;color:#94a3b8">Sent automatically by SpellWise Kids</div>
</div>`,
    weekly: `<!-- MERGE TAGS: {{date}} {{children_html}} -->
<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;padding:24px;color:#1e293b">
  <div style="font-size:24px;font-weight:800;margin-bottom:4px">📋 Weekly Summary</div>
  <div style="font-size:15px;color:#64748b;margin-bottom:24px">{{date}}</div>
  {{children_html}}
  <div style="margin-top:24px;padding-top:16px;border-top:1px solid #e5e7eb;font-size:14px;color:#94a3b8">Sent automatically by SpellWise Kids every Sunday</div>
</div>`,
  };

  const tpls = settings.emailTemplates || {};
  const [tplSpelling, setTplSpelling] = useState(tpls.spelling || DEFAULT_TEMPLATES.spelling);
  const [tplMeaning,  setTplMeaning]  = useState(tpls.meaning  || DEFAULT_TEMPLATES.meaning);
  const [tplWeekly,   setTplWeekly]   = useState(tpls.weekly   || DEFAULT_TEMPLATES.weekly);
  const [tplTab,      setTplTab]      = useState("spelling");
  const [tplSaved,    setTplSaved]    = useState(false);

  const tplMap = { spelling: tplSpelling, meaning: tplMeaning, weekly: tplWeekly };
  const tplSetMap = { spelling: setTplSpelling, meaning: setTplMeaning, weekly: setTplWeekly };

  function saveTemplates() {
    onSettingsChange({ ...settings, emailTemplates: { spelling: tplSpelling, meaning: tplMeaning, weekly: tplWeekly } });
    setTplSaved(true);
    setTimeout(() => setTplSaved(false), 2500);
  }

  function resetTemplate() {
    tplSetMap[tplTab](DEFAULT_TEMPLATES[tplTab]);
  }

  function saveAppName() {
    if (!nameInput.trim()) return;
    onSettingsChange({ ...settings, appName: nameInput.trim() });
    document.title = nameInput.trim();
    setNameSaved(true);
    setTimeout(() => setNameSaved(false), 2500);
  }

  function saveApiKey() {
    onSettingsChange({ ...settings, geminiKey: apiKey.trim(), geminiModel });
    setApiKeySaved(true);
    setTimeout(() => setApiKeySaved(false), 2500);
  }

  const [dailyDigest, setDailyDigest] = useState(settings.resend?.dailyDigest !== false);

  function saveResend() {
    onSettingsChange({ ...settings, rewardWorkerUrl: rewardWorkerUrl.trim().replace(/\/$/, ""), resend: { apiKey: resendApiKey.trim(), fromEmail: resendFrom.trim(), parent1Email: resendParent1.trim(), parent2Email: resendParent2.trim(), dailyDigest } });
    setResendSaved(true);
    setTimeout(() => setResendSaved(false), 2500);
  }

  async function testEmail() {
    setResendTestMsg("Sending…");
    const res = await sendResendEmail(resendApiKey.trim(), resendFrom.trim(), {
      to: [resendParent1.trim(), resendParent2.trim()].filter(Boolean).join(","),
      subject: "SpellWise Kids — Test Email",
      html: "<p>✅ Your SpellWise Kids email notifications are working correctly!</p><p>— SpellWise Kids</p>",
    });
    setResendTestMsg(res.corsWarning ? "✅ Config looks good — CORS blocks direct browser calls to Resend. Emails will send normally from the app." : res.ok ? "✅ Test email sent!" : `❌ Failed: ${res.err}`);
    setTimeout(() => setResendTestMsg(""), 5000);
  }

  const [spellingCount, setSpellingCount] = useState(settings.spellingWordCount || 20);
  const [meaningCount, setMeaningCount]   = useState(settings.meaningWordCount  || 10);
  const [synonymCount, setSynonymCount]   = useState(settings.synonymWordCount  || 10);
  const [antonymCount, setAntonymCount]   = useState(settings.antonymWordCount  || 10);
  const [countSaved, setCountSaved]       = useState(false);

  function saveWordCounts() {
    const sc = Math.min(50, Math.max(5, parseInt(spellingCount) || 20));
    const mc = Math.min(30, Math.max(3, parseInt(meaningCount)  || 10));
    const syc = Math.max(3, parseInt(synonymCount) || 10);
    const anc = Math.max(3, parseInt(antonymCount) || 10);
    setSpellingCount(sc); setMeaningCount(mc); setSynonymCount(syc); setAntonymCount(anc);
    onSettingsChange({ ...settings, spellingWordCount: sc, meaningWordCount: mc, synonymWordCount: syc, antonymWordCount: anc });
    setCountSaved(true); setTimeout(() => setCountSaved(false), 2500);
  }

  const resendConfigured = resendApiKey && resendFrom && (resendParent1 || resendParent2);

  return (
    <div className="space-y-6">
      {/* APP NAME */}
      <div className="index-card rounded-xl p-5">
        <div className="font-handwritten text-2xl text-stone-800 mb-4">App Name</div>
        <div className="text-sm text-stone-600 mb-3">This name appears on the login screen and browser tab.</div>
        <div className="flex gap-3 items-center flex-wrap">
          <input
            className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
            style={{ maxWidth: 280 }}
            value={nameInput}
            onChange={e => { setNameInput(e.target.value); setNameSaved(false); }}
            onKeyDown={e => e.key === "Enter" && saveAppName()}
            placeholder="SpellWise Kids"
          />
          <button
            className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600"
            onClick={saveAppName}
          >
            Save
          </button>
          {nameSaved && <span className="text-xs text-green-600 font-semibold">Saved ✓</span>}
        </div>
      </div>

      {/* WORD COUNT SETTINGS */}
      <div className="index-card rounded-xl p-5">
        <div className="font-handwritten text-2xl text-stone-800 mb-4">Test Word Counts</div>
        <div className="text-sm text-stone-600 mb-4">Control how many words appear in each session. Spelling: 5–50. Meaning: 3–30. Synonyms/Antonyms: 3+.</div>
        <div className="flex gap-5 flex-wrap mb-4">
          <div>
            <div className="font-semibold text-sm text-stone-700 mb-2 uppercase tracking-wide">✏️ Spelling Test</div>
            <div className="flex items-center gap-2">
              <button
                className="w-8 h-8 rounded border border-stone-300 hover:bg-stone-100 text-sm flex items-center justify-center"
                onClick={() => setSpellingCount(c => Math.max(5, c - 1))}
              >
                −
              </button>
              <input
                className="w-16 px-2 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white text-center font-bold text-base"
                type="number"
                min={5}
                max={50}
                value={spellingCount}
                onChange={e => setSpellingCount(Math.min(50, Math.max(5, parseInt(e.target.value) || 5)))}
              />
              <button
                className="w-8 h-8 rounded border border-stone-300 hover:bg-stone-100 text-sm flex items-center justify-center"
                onClick={() => setSpellingCount(c => Math.min(50, c + 1))}
              >
                +
              </button>
              <span className="text-xs text-stone-600">words</span>
            </div>
          </div>
          <div>
            <div className="font-semibold text-sm text-stone-700 mb-2 uppercase tracking-wide">📖 Meaning Quiz</div>
            <div className="flex items-center gap-2">
              <button
                className="w-8 h-8 rounded border border-stone-300 hover:bg-stone-100 text-sm flex items-center justify-center"
                onClick={() => setMeaningCount(c => Math.max(3, c - 1))}
              >
                −
              </button>
              <input
                className="w-16 px-2 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white text-center font-bold text-base"
                type="number"
                min={3}
                max={30}
                value={meaningCount}
                onChange={e => setMeaningCount(Math.min(30, Math.max(3, parseInt(e.target.value) || 3)))}
              />
              <button
                className="w-8 h-8 rounded border border-stone-300 hover:bg-stone-100 text-sm flex items-center justify-center"
                onClick={() => setMeaningCount(c => Math.min(30, c + 1))}
              >
                +
              </button>
              <span className="text-xs text-stone-600">words</span>
            </div>
          </div>
          <div>
            <div className="font-semibold text-sm text-stone-700 mb-2 uppercase tracking-wide">🔤 Synonyms Quiz</div>
            <div className="flex items-center gap-2">
              <button
                className="w-8 h-8 rounded border border-stone-300 hover:bg-stone-100 text-sm flex items-center justify-center"
                onClick={() => setSynonymCount(c => Math.max(3, c - 1))}
              >
                −
              </button>
              <input
                className="w-16 px-2 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white text-center font-bold text-base"
                type="number"
                min={3}
                value={synonymCount}
                onChange={e => setSynonymCount(Math.max(3, parseInt(e.target.value) || 3))}
              />
              <button
                className="w-8 h-8 rounded border border-stone-300 hover:bg-stone-100 text-sm flex items-center justify-center"
                onClick={() => setSynonymCount(c => c + 1)}
              >
                +
              </button>
              <span className="text-xs text-stone-600">words</span>
            </div>
          </div>
          <div>
            <div className="font-semibold text-sm text-stone-700 mb-2 uppercase tracking-wide">🔄 Antonyms Quiz</div>
            <div className="flex items-center gap-2">
              <button
                className="w-8 h-8 rounded border border-stone-300 hover:bg-stone-100 text-sm flex items-center justify-center"
                onClick={() => setAntonymCount(c => Math.max(3, c - 1))}
              >
                −
              </button>
              <input
                className="w-16 px-2 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white text-center font-bold text-base"
                type="number"
                min={3}
                value={antonymCount}
                onChange={e => setAntonymCount(Math.max(3, parseInt(e.target.value) || 3))}
              />
              <button
                className="w-8 h-8 rounded border border-stone-300 hover:bg-stone-100 text-sm flex items-center justify-center"
                onClick={() => setAntonymCount(c => c + 1)}
              >
                +
              </button>
              <span className="text-xs text-stone-600">words</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600"
            onClick={saveWordCounts}
          >
            Save
          </button>
          {countSaved && <span className="text-xs text-green-600 font-semibold">Saved ✓</span>}
        </div>
      </div>

      {/* GOOGLE GEMINI API KEY */}
      <div className="index-card rounded-xl p-5">
        <div className="font-handwritten text-2xl text-stone-800 mb-4">Google Gemini API Key</div>
        <div className="text-sm text-stone-600 mb-4">
          Required for scanning word list photos. Get your key at <strong>aistudio.google.com</strong>.
        </div>

        <div className="flex gap-3 items-center flex-wrap mb-3">
          <div className="relative flex-1" style={{ maxWidth: 340 }}>
            <input
              className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
              type={showKey ? "text" : "password"}
              value={apiKey}
              onChange={e => { setApiKey(e.target.value); setApiKeySaved(false); }}
              onKeyDown={e => e.key === "Enter" && saveApiKey()}
              placeholder="AIza…"
              style={{ paddingRight: "2.5rem" }}
            />
            <button
              onClick={() => setShowKey(s => !s)}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-none border-none cursor-pointer text-sm text-stone-600 hover:text-stone-800"
            >
              {showKey ? "🙈" : "👁️"}
            </button>
          </div>
          <button
            className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600"
            onClick={saveApiKey}
          >
            Save
          </button>
          <button
            className="px-4 py-2 bg-stone-100 text-stone-700 rounded-lg text-sm font-medium hover:bg-stone-200 border border-stone-200"
            onClick={testGeminiConnection}
            disabled={testStatus === "testing"}
          >
            {testStatus === "testing" ? "⏳ Testing…" : "🧪 Test"}
          </button>
          {apiKeySaved && <span className="text-xs text-green-600 font-semibold">Saved ✓</span>}
        </div>

        {testMsg && (
          <div className={`text-xs font-semibold mb-3 ${testStatus === "ok" ? "text-green-600" : "text-red-600"}`}>
            {testMsg}
          </div>
        )}

        {!testMsg && (settings.geminiKey
          ? <div className="text-xs text-green-600 mb-3 font-semibold">✅ API key saved — image upload is enabled</div>
          : <div className="text-xs text-amber-600 mb-3 font-semibold">⚠️ No key saved — image upload won't work until you add one</div>)}

        <div className="flex items-center gap-3 mb-3">
          <div className="font-semibold text-sm text-stone-700 uppercase tracking-wide">Model</div>
          <button
            className="px-3 py-1.5 bg-stone-100 text-stone-700 rounded-lg text-xs font-medium hover:bg-stone-200 border border-stone-200"
            onClick={() => fetchGeminiModels(apiKey || settings.geminiKey)}
            disabled={modelsLoading || (!apiKey && !settings.geminiKey)}
          >
            {modelsLoading ? "⏳ Loading…" : "🔄 Refresh"}
          </button>
          {modelsErr && <span className="text-xs text-amber-600">{modelsErr}</span>}
        </div>
        <div className="border-2 border-stone-300 rounded-lg overflow-hidden" style={{ maxHeight: 260, overflowY: "auto" }}>
          {displayModels.map((m, i) => {
            const selected = geminiModel === m.id;
            return (
              <div
                key={m.id}
                onClick={() => setGeminiModel(m.id)}
                className={`flex items-center gap-3 p-3 cursor-pointer transition-colors ${
                  selected
                    ? "bg-amber-50 border-l-4 border-l-amber-500"
                    : i % 2 === 0 ? "bg-white border-l-4 border-l-transparent" : "bg-stone-50 border-l-4 border-l-transparent"
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className={`font-bold text-sm whitespace-nowrap overflow-hidden text-ellipsis ${selected ? "text-amber-600" : "text-stone-800"}`}>{m.label}</div>
                  {m.sub && <div className="text-xs text-stone-600 whitespace-nowrap overflow-hidden text-ellipsis">{m.sub}</div>}
                </div>
                {selected && <div className="text-xs font-bold text-amber-600 flex-shrink-0">✓ Selected</div>}
              </div>
            );
          })}
        </div>
        {!settings.geminiKey && !apiKey && <div className="text-xs text-stone-600 mt-2">Save your API key first to load available models live from Google.</div>}
      </div>

      {/* RESEND */}
      <div className="index-card rounded-xl p-5">
        <div className="font-handwritten text-2xl text-stone-800 mb-4">Email Notifications (Resend)</div>
        <div className="text-sm text-stone-600 mb-4 leading-relaxed">
          Sends a report after each test. Weekly summaries are handled automatically by the Cloudflare Worker cron.<br />
          <strong>Setup:</strong> Create a free account at <strong>resend.com</strong> → Add a verified domain → create an API key and paste it below.
        </div>

        <div className="space-y-3 mb-4">
          <div>
            <div className="font-semibold text-sm text-stone-700 mb-2 uppercase tracking-wide">Resend API Key</div>
            <input
              className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
              type="password"
              placeholder="re_xxxxxxxxxxxxxxxx"
              value={resendApiKey}
              onChange={e => setResendApiKey(e.target.value)}
              style={{ maxWidth: 340 }}
            />
          </div>
          <div>
            <div className="font-semibold text-sm text-stone-700 mb-2 uppercase tracking-wide">From Email</div>
            <input
              className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
              type="email"
              placeholder="noreply@yourdomain.com"
              value={resendFrom}
              onChange={e => setResendFrom(e.target.value)}
              style={{ maxWidth: 320 }}
            />
          </div>
          <div>
            <div className="font-semibold text-sm text-stone-700 mb-2 uppercase tracking-wide">Parent 1 Email</div>
            <input
              className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
              type="email"
              placeholder="parent1@email.com"
              value={resendParent1}
              onChange={e => setResendParent1(e.target.value)}
              style={{ maxWidth: 320 }}
            />
          </div>
          <div>
            <div className="font-semibold text-sm text-stone-700 mb-2 uppercase tracking-wide">Parent 2 Email</div>
            <input
              className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
              type="email"
              placeholder="parent2@email.com"
              value={resendParent2}
              onChange={e => setResendParent2(e.target.value)}
              style={{ maxWidth: 320 }}
            />
          </div>
          <div>
            <div className="font-semibold text-sm text-stone-700 mb-2 uppercase tracking-wide">Reward Worker URL <span className="font-normal text-stone-600">(for reward approve/decline links)</span></div>
            <input
              className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
              placeholder="https://your-worker.workers.dev"
              value={rewardWorkerUrl}
              onChange={e => setRewardWorkerUrl(e.target.value)}
              style={{ maxWidth: 380 }}
            />
            <div className="text-xs text-stone-600 mt-1">Your Cloudflare Worker URL — used to generate Approve/Decline links in reward request emails.</div>
          </div>
          <div className="flex gap-3 items-center flex-wrap pt-2">
            <button
              className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600"
              onClick={saveResend}
            >
              Save Email Settings
            </button>
            {resendSaved && <span className="text-xs text-green-600 font-semibold">Saved ✓</span>}
            {resendConfigured && <button
              className="px-4 py-2 bg-stone-100 text-stone-700 rounded-lg text-sm font-medium hover:bg-stone-200 border border-stone-200"
              onClick={testEmail}
            >
              Send Test Email
            </button>}
            {resendTestMsg && <span className={`text-sm font-semibold ${resendTestMsg.startsWith("✅") ? "text-green-600" : "text-red-600"}`}>{resendTestMsg}</span>}
          </div>
        </div>
        <div className={`text-xs font-semibold ${resendConfigured ? "text-green-600" : "text-amber-600"}`}>
          {resendConfigured ? "✅ Email notifications configured" : "⚠️ Email notifications not yet configured"}
        </div>

        {/* Daily Digest toggle */}
        <div className="mt-4 flex items-center gap-3">
          <button
            type="button"
            onClick={() => setDailyDigest(d => !d)}
            className={`w-11 h-6 rounded-full border-none cursor-pointer relative transition-colors flex-shrink-0 ${dailyDigest ? "bg-green-600" : "bg-stone-300"}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${dailyDigest ? "translate-x-5" : "translate-x-0"}`} />
          </button>
          <div>
            <div className="text-sm font-bold text-stone-800">Daily Digest</div>
            <div className="text-xs text-stone-600">Send one consolidated email at 11 pm AEDT covering all quizzes done that day (requires daily-digest Cloudflare Worker)</div>
          </div>
        </div>
      </div>

      {/* EMAIL TEMPLATES */}
      <div className="index-card rounded-xl p-5">
        <div className="font-handwritten text-2xl text-stone-800 mb-4">Email Templates</div>
        <div className="text-sm text-stone-600 mb-4 leading-relaxed">
          Full HTML editor. Edit code on the left, see a live rendered preview on the right with sample data substituted for all merge tags.
        </div>

        <div className="flex gap-2 mb-4 flex-wrap">
          {[["spelling","✏️ Spelling"], ["meaning","📖 Meaning"], ["weekly","📋 Weekly"]].map(([k,l]) => (
            <button
              key={k}
              className={`binder-tab px-3 py-2 text-xs font-medium rounded transition-colors ${
                tplTab === k
                  ? "bg-amber-500 text-white"
                  : "bg-stone-100 text-stone-700 hover:bg-stone-200"
              }`}
              onClick={() => setTplTab(k)}
            >
              {l}
            </button>
          ))}
        </div>

        <div className="bg-stone-50 rounded-lg p-3 mb-4 text-xs text-stone-700 leading-relaxed">
          <strong className="text-stone-800">Merge tags: </strong>
          {tplTab !== "weekly" && <>
            <code className="bg-stone-200 px-1 rounded text-xs">{"{{child_avatar}}"}</code> <code className="bg-stone-200 px-1 rounded text-xs">{"{{child_name}}"}</code> <code className="bg-stone-200 px-1 rounded text-xs">{"{{quiz_type}}"}</code> <code className="bg-stone-200 px-1 rounded text-xs">{"{{score}}"}</code> <code className="bg-stone-200 px-1 rounded text-xs">{"{{score_color}}"}</code> <code className="bg-stone-200 px-1 rounded text-xs">{"{{time}}"}</code> <code className="bg-stone-200 px-1 rounded text-xs">{"{{pts_earned}}"}</code> <code className="bg-stone-200 px-1 rounded text-xs">{"{{date}}"}</code> <code className="bg-stone-200 px-1 rounded text-xs">{"{{words_to_review_html}}"}</code>
            {tplTab === "spelling" && <> <code className="bg-stone-200 px-1 rounded text-xs">{"{{mastered_html}}"}</code></>}
          </>}
          {tplTab === "weekly" && <><code className="bg-stone-200 px-1 rounded text-xs">{"{{date}}"}</code> <code className="bg-stone-200 px-1 rounded text-xs">{"{{children_html}}"}</code></>}
        </div>

        <div className="flex gap-1 flex-wrap bg-stone-50 rounded-t-lg p-2 border-2 border-stone-300 border-b-0">
          {[
            ["B", (t) => `<strong>${t}</strong>`],
            ["I", (t) => `<em>${t}</em>`],
            ["U", (t) => `<u>${t}</u>`],
            ["H1", (t) => `<h1>${t}</h1>`],
            ["H2", (t) => `<h2>${t}</h2>`],
            ["H3", (t) => `<h3>${t}</h3>`],
          ].map(([label, wrapper]) => (
            <button
              key={label}
              type="button"
              title={`Wrap selection in ${label}`}
              onClick={() => {
                const ta = document.getElementById(`tpl-editor-${tplTab}`);
                if (!ta) return;
                const start = ta.selectionStart, end = ta.selectionEnd;
                const sel = ta.value.slice(start, end) || "text";
                const wrapped = wrapper(sel);
                const newVal = ta.value.slice(0, start) + wrapped + ta.value.slice(end);
                tplSetMap[tplTab](newVal);
              }}
              className="typewriter-key font-bold text-xs px-2 py-1 rounded border border-stone-300 bg-white hover:bg-stone-100 cursor-pointer min-w-8 text-center"
            >
              {label}
            </button>
          ))}
          <div className="w-px bg-stone-300 mx-1" />
          {[
            ["16px", (t) => `<span style="font-size:16px">${t}</span>`],
            ["18px", (t) => `<span style="font-size:18px">${t}</span>`],
            ["22px", (t) => `<span style="font-size:22px">${t}</span>`],
          ].map(([label, wrapper]) => (
            <button
              key={label}
              type="button"
              onClick={() => {
                const ta = document.getElementById(`tpl-editor-${tplTab}`);
                if (!ta) return;
                const start = ta.selectionStart, end = ta.selectionEnd;
                const sel = ta.value.slice(start, end) || "text";
                const newVal = ta.value.slice(0, start) + wrapper(sel) + ta.value.slice(end);
                tplSetMap[tplTab](newVal);
              }}
              className="typewriter-key text-xs px-2 py-1 rounded border border-stone-300 bg-white hover:bg-stone-100 cursor-pointer"
            >
              {label}
            </button>
          ))}
          <div className="ml-auto flex gap-1">
            <button
              type="button"
              title="Clean HTML (remove empty tags)"
              onClick={() => {
                let cleaned = tplMap[tplTab]
                  .replace(/<(\w+)[^>]*>\s*<\/\1>/g, "")
                  .replace(/\n{3,}/g, "\n\n")
                  .trim();
                tplSetMap[tplTab](cleaned);
              }}
              className="text-xs px-2 py-1 rounded border border-stone-300 bg-white hover:bg-stone-100 cursor-pointer text-stone-600"
            >
              🧹 Clean
            </button>
          </div>
        </div>

        <div className="flex gap-0 border-2 border-stone-300 border-t-0 rounded-b-lg overflow-hidden" style={{ minHeight: 340 }}>
          <div className="flex-1 flex flex-col border-r-2 border-r-stone-300">
            <div className="bg-stone-50 px-3 py-1 text-xs text-stone-600 font-bold border-b-2 border-b-stone-300">HTML</div>
            <textarea
              id={`tpl-editor-${tplTab}`}
              value={tplMap[tplTab]}
              onChange={e => tplSetMap[tplTab](e.target.value)}
              spellCheck={false}
              className="flex-1 font-mono text-xs p-3 border-none bg-slate-900 text-slate-100 resize-none outline-none"
              style={{ minHeight: 320, lineHeight: 1.55 }}
            />
          </div>
          <div className="flex-1 flex flex-col">
            <div className="bg-stone-50 px-3 py-1 text-xs text-stone-600 font-bold border-b-2 border-b-stone-300">Preview (sample data)</div>
            <iframe
              title={`email-preview-${tplTab}`}
              srcDoc={(() => {
                const sampleWordsHtml = `<div style="margin-top:28px"><div style="font-size:20px;font-weight:800;color:#1e293b;margin-bottom:12px">📚 Words to Review</div><table style="width:100%;border-collapse:collapse"><tr><td style="padding:10px 0;border-bottom:1px solid #e5e7eb"><div style="font-size:18px;font-weight:700;color:#1e293b">accommodation</div><div style="font-size:16px;color:#64748b;margin-top:4px">→ the process of adapting or adjusting to something</div></td></tr><tr><td style="padding:10px 0;border-bottom:1px solid #e5e7eb"><div style="font-size:18px;font-weight:700;color:#1e293b">perseverance</div><div style="font-size:16px;color:#64748b;margin-top:4px">→ continued effort despite difficulty or delay</div></td></tr></table></div>`;
                const sampleMasteredHtml = `<div style="margin-top:20px;padding:14px 16px;background:#dcfce7;border-radius:10px;font-size:16px;color:#15803d;font-weight:600">🏆 Newly mastered: rhythm, exaggerate</div>`;
                const sampleChildrenHtml = `<div style="background:#f8fafc;border-radius:12px;padding:20px;margin-bottom:16px"><div style="font-size:20px;font-weight:800;margin-bottom:12px">🌸 Maisha <span style="font-size:15px;color:#64748b;font-weight:600">Year 8</span></div><div style="font-size:15px;color:#475569">🔥 Streak: <strong>5 days</strong> &nbsp;|&nbsp; 🏆 Mastered: <strong>12/20 words</strong></div></div><div style="background:#f8fafc;border-radius:12px;padding:20px;margin-bottom:16px"><div style="font-size:20px;font-weight:800;margin-bottom:12px">⚡ Aariv <span style="font-size:15px;color:#64748b;font-weight:600">Year 6</span></div><div style="font-size:15px;color:#475569">🔥 Streak: <strong>3 days</strong> &nbsp;|&nbsp; 🏆 Mastered: <strong>8/20 words</strong></div></div>`;
                return tplMap[tplTab]
                  .replace(/\{\{child_avatar\}\}/g, "🌸")
                  .replace(/\{\{child_name\}\}/g, "Maisha")
                  .replace(/\{\{quiz_type\}\}/g, tplTab === "meaning" ? "Meaning Quiz" : "Spelling Test")
                  .replace(/\{\{score\}\}/g, "85")
                  .replace(/\{\{score_color\}\}/g, "#16a34a")
                  .replace(/\{\{time\}\}/g, "4m 32s")
                  .replace(/\{\{pts_earned\}\}/g, "24")
                  .replace(/\{\{date\}\}/g, new Date().toLocaleDateString("en-AU", { weekday:"long", day:"numeric", month:"long" }))
                  .replace(/\{\{mastered_html\}\}/g, sampleMasteredHtml)
                  .replace(/\{\{words_to_review_html\}\}/g, sampleWordsHtml)
                  .replace(/\{\{children_html\}\}/g, sampleChildrenHtml);
              })()}
              className="flex-1 border-none bg-white"
              style={{ minHeight: 320 }}
              sandbox="allow-same-origin"
            />
          </div>
        </div>

        <div className="flex gap-2 items-center mt-4 flex-wrap">
          <button
            className="text-sm text-amber-600 hover:text-amber-700 underline cursor-pointer"
            onClick={resetTemplate}
          >
            ↩ Reset to Default
          </button>
          <button
            className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600"
            onClick={saveTemplates}
          >
            Save Templates
          </button>
          {tplSaved && <span className="text-xs text-green-600 font-semibold">Saved ✓</span>}
        </div>
      </div>

      {/* CREDENTIALS */}
      <div className="index-card rounded-xl p-5">
        <div className="font-handwritten text-2xl text-stone-800 mb-4">Account Credentials</div>
        <div className="space-y-4">
          {children.map(c => (
            <div key={c.id} className="p-4 bg-stone-50 rounded-lg">
              <div className="font-bold text-stone-800">{c.avatar} {c.name} — Year {c.yearLevel}</div>
              <div className="text-sm text-stone-600 mt-2">
                PIN: <code className="bg-stone-200 px-2 py-0.5 rounded text-xs">{c.pin || "1234"}</code>
                <span className="text-xs ml-3">(children set their own PIN on first login)</span>
                {c.mustResetPin && <span className="ml-2 text-xs text-amber-600 font-bold">⚠️ PIN reset pending</span>}
              </div>
              <div className="mt-4">
                <div className="font-semibold text-sm text-stone-700 mb-2 uppercase tracking-wide">Child Email (for Words to Review)</div>
                <div className="flex gap-2 items-center">
                  <input
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
                    type="email"
                    placeholder={`${c.name.toLowerCase()}@email.com`}
                    value={settings.childEmails?.[c.id] || ""}
                    onChange={e => onSettingsChange({ ...settings, childEmails: { ...(settings.childEmails || {}), [c.id]: e.target.value } })}
                    style={{ maxWidth: 280 }}
                  />
                </div>
              </div>
            </div>
          ))}
          <div className="p-4 bg-stone-50 rounded-lg">
            <div className="font-bold text-stone-800">🏠 Parent</div>
            <div className="text-sm text-stone-600 mt-2">PIN: <code className="bg-stone-200 px-2 py-0.5 rounded text-xs">{users.find(u => u.role === "parent")?.pin}</code></div>
          </div>
        </div>
      </div>

      {/* DANGER ZONE */}
      <div className="index-card rounded-xl p-5 border-2 border-red-300">
        <div className="font-bold text-red-600 mb-3">Danger Zone</div>
        <button
          className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600"
          onClick={() => { if (window.confirm("Reset ALL data?")) { localStorage.removeItem("spellwise_v3"); window.location.reload(); } }}
        >
          Reset All App Data
        </button>
      </div>

      {/* FOOTER */}
      <div className="pt-4 border-t-2 border-t-stone-300">
        <VersionTag />
        <div className="text-xs text-stone-600 mt-2">
          {DEMO_MODE ? "⚠️ Firebase not connected — data saved locally in browser." : "✅ Firebase Realtime Database connected."}
        </div>
      </div>
    </div>
  );
}
